import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, Filter, ChevronDown, Palette, Ruler } from 'lucide-react';
import { products, categories, subCategories, searchProducts } from '../data/products';
import { useTheme } from '../context/ThemeContext';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_4a46bb01-e425-48c8-a790-c185a7fd0b57/artifacts/6wdpibd5_logomedusa.jpeg';

const randomProduct = products[Math.floor(Math.random() * products.length)];
const randomImage = randomProduct.images[Math.floor(Math.random() * randomProduct.images.length)];

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

export const Products = () => {
  const { isDarkMode } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [selectedSubCategory, setSelectedSubCategory] = useState(searchParams.get('sub') || 'all');
  const [showFilters, setShowFilters] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const category = searchParams.get('category');
    const sub = searchParams.get('sub');
    if (category) setSelectedCategory(category);
    if (sub) setSelectedSubCategory(sub);
  }, [searchParams]);

  const filteredProducts = useMemo(() => {
    let result = products;

    if (searchQuery) {
      result = searchProducts(searchQuery);
    }

    if (selectedCategory !== 'all') {
      result = result.filter(p => p.category === selectedCategory);
    }

    if (selectedSubCategory !== 'all') {
      result = result.filter(p => p.subCategory === selectedSubCategory);
    }

    return result;
  }, [searchQuery, selectedCategory, selectedSubCategory]);

  // Group products by subcategory
  const groupedProducts = useMemo(() => {
    const groups = {};
    filteredProducts.forEach(product => {
      const key = `${product.category}-${product.subCategory}`;
      if (!groups[key]) {
        groups[key] = {
          category: product.category,
          subCategory: product.subCategory,
          products: []
        };
      }
      groups[key].products.push(product);
    });
    return Object.values(groups);
  }, [filteredProducts]);

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setSelectedSubCategory('all');
    if (category === 'all') {
      searchParams.delete('category');
      searchParams.delete('sub');
    } else {
      searchParams.set('category', category);
      searchParams.delete('sub');
    }
    setSearchParams(searchParams);
  };

  const handleSubCategoryChange = (subCat) => {
    setSelectedSubCategory(subCat);
    if (subCat === 'all') {
      searchParams.delete('sub');
    } else {
      searchParams.set('sub', subCat);
    }
    setSearchParams(searchParams);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('all');
    setSelectedSubCategory('all');
    setSearchParams({});
  };

  const currentSubCategories = selectedCategory !== 'all' ? subCategories[selectedCategory] : [];

  return (
    <div className={`pt-24 md:pt-28 min-h-screen ${isDarkMode ? 'bg-zinc-900' : ''}`} data-testid="products-page">
      {/* Hero Banner with Logo Background */}
      <section className={`py-16 md:py-24 relative overflow-hidden ${isDarkMode ? 'bg-zinc-800' : 'bg-white'}`}>
        {/* Logo Background */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <img 
            src={LOGO_URL} 
            alt="" 
            className="w-full h-full object-contain opacity-[0.06]"
            style={{
              filter: isDarkMode ? 'invert(1) sepia(0.5)' : 'none',
              maxWidth: '60%',
              maxHeight: '80%'
            }}
          />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            className="text-center max-w-2xl mx-auto"
            initial="initial"
            animate="animate"
            variants={fadeUp}
          >
            <p className={`section-subtitle ${isDarkMode ? 'text-gray-400' : ''}`}>Design Collection</p>
            <h1 
              className={`text-3xl sm:text-4xl lg:text-5xl mb-6 ${isDarkMode ? 'text-white' : ''}`}
              style={{ fontFamily: 'Bodoni Moda, serif' }}
            >
              Our Designs
            </h1>
            <p className={`mb-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Browse our premium uniform designs. Every piece customized as per your measurement and selected colour.
            </p>

            {/* Search Bar - Unique design for mobile */}
            <div className="relative max-w-md mx-auto">
              {/* Mobile: Logo watermark in search */}
              {isMobile && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden rounded-lg">
                  <img
                    src={randomImage}
                    alt=""
                    className="w-full h-full object-cover opacity-20 blur-[1px]"
                  />
                </div>
              )}
              <div className={`relative flex items-center ${
                isMobile 
                  ? 'bg-white/90 backdrop-blur-sm' 
                  : isDarkMode ? 'bg-zinc-900' : 'bg-white'
              } border ${isDarkMode ? 'border-zinc-700' : 'border-gray-200'} rounded-lg overflow-hidden`}>
                <Search size={20} className={`ml-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} />
                <input
                  type="text"
                  placeholder="Search designs..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`w-full px-4 py-4 bg-transparent outline-none ${isDarkMode ? 'text-white placeholder-gray-500' : ''}`}
                  data-testid="search-input"
                />
                {searchQuery && (
                  <button 
                    onClick={() => setSearchQuery('')}
                    className={`mr-4 ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-400 hover:text-black'}`}
                    data-testid="clear-search"
                  >
                    <X size={18} />
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Filters and Products */}
      <section className="py-12 md:py-16">
        <div className="container-custom">
          {/* Mobile Filter Toggle */}
          <div className="lg:hidden mb-6">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`w-full flex items-center justify-between px-4 py-3 border ${
                isDarkMode ? 'border-zinc-700 bg-zinc-800 text-white' : 'border-gray-200'
              }`}
              data-testid="filter-toggle"
            >
              <span className="flex items-center gap-2 text-sm uppercase tracking-wider">
                <Filter size={16} />
                Categories
              </span>
              <ChevronDown size={16} className={`transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar Filters */}
            <AnimatePresence>
              {(showFilters || !isMobile) && (
                <motion.aside
                  className="lg:w-64 flex-shrink-0"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="sticky top-28 space-y-8">
                    {/* Categories */}
                    <div>
                      <h3 className={`text-xs uppercase tracking-widest font-medium mb-4 ${isDarkMode ? 'text-white' : ''}`}>Categories</h3>
                      <div className="space-y-2">
                        <button
                          onClick={() => handleCategoryChange('all')}
                          className={`block w-full text-left text-sm py-2 px-3 transition-colors ${
                            selectedCategory === 'all' 
                              ? isDarkMode ? 'bg-white text-black' : 'bg-black text-white'
                              : isDarkMode ? 'hover:bg-zinc-800 text-white' : 'hover:bg-gray-100'
                          }`}
                          data-testid="filter-all"
                        >
                          All Designs
                        </button>
                        {categories.map((cat) => (
                          <button
                            key={cat.id}
                            onClick={() => handleCategoryChange(cat.id)}
                            className={`block w-full text-left text-sm py-2 px-3 transition-colors ${
                              selectedCategory === cat.id 
                                ? isDarkMode ? 'bg-white text-black' : 'bg-black text-white'
                                : isDarkMode ? 'hover:bg-zinc-800 text-white' : 'hover:bg-gray-100'
                            }`}
                            data-testid={`filter-${cat.id}`}
                          >
                            {cat.name}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Sub-categories */}
                    {currentSubCategories.length > 0 && (
                      <div>
                        <h3 className={`text-xs uppercase tracking-widest font-medium mb-4 ${isDarkMode ? 'text-white' : ''}`}>Type</h3>
                        <div className="space-y-2">
                          <button
                            onClick={() => handleSubCategoryChange('all')}
                            className={`block w-full text-left text-sm py-2 px-3 transition-colors ${
                              selectedSubCategory === 'all' 
                                ? isDarkMode ? 'bg-white text-black' : 'bg-black text-white'
                                : isDarkMode ? 'hover:bg-zinc-800 text-white' : 'hover:bg-gray-100'
                            }`}
                            data-testid="filter-sub-all"
                          >
                            All Types
                          </button>
                          {currentSubCategories.map((sub) => (
                            <button
                              key={sub.id}
                              onClick={() => handleSubCategoryChange(sub.id)}
                              className={`block w-full text-left text-sm py-2 px-3 transition-colors ${
                                selectedSubCategory === sub.id 
                                  ? isDarkMode ? 'bg-white text-black' : 'bg-black text-white'
                                  : isDarkMode ? 'hover:bg-zinc-800 text-white' : 'hover:bg-gray-100'
                              }`}
                              data-testid={`filter-sub-${sub.id}`}
                            >
                              {sub.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Clear Filters */}
                    {(selectedCategory !== 'all' || selectedSubCategory !== 'all' || searchQuery) && (
                      <button
                        onClick={clearFilters}
                        className={`text-xs uppercase tracking-wider transition-colors ${
                          isDarkMode ? 'text-gray-500 hover:text-white' : 'text-gray-500 hover:text-black'
                        }`}
                        data-testid="clear-filters"
                      >
                        Clear all filters
                      </button>
                    )}
                  </div>
                </motion.aside>
              )}
            </AnimatePresence>

            {/* Products Grid */}
            <div className="flex-1">
              {/* Results Count */}
              <div className="flex justify-between items-center mb-8">
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Showing {filteredProducts.length} design{filteredProducts.length !== 1 ? 's' : ''}
                </p>
              </div>

              {filteredProducts.length > 0 ? (
                <div className="space-y-16">
                  {groupedProducts.map((group) => (
                    <div key={`${group.category}-${group.subCategory}`}>
                      {/* Group Header */}
                      <div className="mb-8">
                        <p className={`text-xs uppercase tracking-widest mb-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          {group.category === 'hotel' ? 'Hotel Staff' : 'Hospital Staff'}
                        </p>
                        <h2 
                          className={`text-2xl md:text-3xl ${isDarkMode ? 'text-white' : ''}`}
                          style={{ fontFamily: 'Bodoni Moda, serif' }}
                        >
                          {subCategories[group.category]?.find(s => s.id === group.subCategory)?.name} Designs
                        </h2>
                        <p className={`text-sm mt-2 flex items-center gap-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          <span className="flex items-center gap-1"><Ruler size={14} /> As per your measurement</span>
                          <span className="flex items-center gap-1"><Palette size={14} /> As per your colour</span>
                        </p>
                      </div>

                      {/* Products Grid - 4 per row */}
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                        {group.products.slice(0, 4).map((product, index) => (
                          <motion.div
                            key={product.id}
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: index * 0.05 }}
                          >
                            <Link
                              to={`/products/${product.id}`}
                              className="product-card block group"
                              data-testid={`product-${product.id}`}
                            >
                              <div className={`overflow-hidden aspect-[3/4] ${isDarkMode ? 'bg-zinc-800' : 'bg-gray-100'}`}>
                                <img
                                  src={product.images[0]}
                                  alt={product.name}
                                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                  loading="lazy"
                                />
                              </div>
                              <div className={`p-4 border border-t-0 ${
                                isDarkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-100'
                              }`}>
                                <h3 
                                  className={`text-sm md:text-base group-hover:text-[#D4AF37] transition-colors ${isDarkMode ? 'text-white' : ''}`}
                                  style={{ fontFamily: 'Bodoni Moda, serif' }}
                                >
                                  {product.name}
                                </h3>
                                <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                  Customizable Design
                                </p>
                              </div>
                            </Link>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <motion.div
                  className="text-center py-20"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <p className={`mb-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>No designs found matching your criteria.</p>
                  <button
                    onClick={clearFilters}
                    className={`btn-secondary ${isDarkMode ? 'border-white text-white hover:bg-white hover:text-black' : ''}`}
                    data-testid="reset-search"
                  >
                    Reset Filters
                  </button>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className={`py-16 md:py-24 ${isDarkMode ? 'bg-zinc-800' : 'bg-[#0a0a0a]'} text-white`}>
        <div className="container-custom text-center">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <h2 
              className="text-2xl md:text-3xl mb-6"
              style={{ fontFamily: 'Bodoni Moda, serif' }}
            >
              Found a Design You Like?
            </h2>
            <p className="text-gray-400 mb-8 max-w-lg mx-auto">
              Contact us to customize it as per your measurement and colour preference. We deal in customization only.
            </p>
            <a
              href="https://wa.me/916398130510?text=Hello%20MALABISthestic!%20I%20found%20a%20design%20I%20like%20and%20want%20to%20customize%20it."
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center gap-3"
              data-testid="custom-order-cta"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Customize via WhatsApp
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Products;
